import React from 'react';
import { useTranslation } from 'react-i18next';
import { CHAT_PANEL, LAYOUT, Z_INDEX } from '@constants/layout';

interface ChatResizeHandleProps {
  chatWidth: number;
  handleProps: React.ComponentProps<'div'>;
}

const GripDot: React.FC = () => (
  <span className="size-[3px] rounded-full bg-muted-foreground/70 transition-colors duration-150 ease-shell group-hover:bg-primary" />
);

/** Sits on the chat panel's left edge; drag or arrow-key to resize the column. */
const ChatResizeHandle: React.FC<ChatResizeHandleProps> = ({ chatWidth, handleProps }) => {
  const { t } = useTranslation();

  return (
    <div
      role="separator"
      tabIndex={0}
      aria-orientation="vertical"
      aria-label={t('navigation.resizeChat')}
      aria-valuenow={chatWidth}
      aria-valuemin={CHAT_PANEL.MIN_WIDTH}
      aria-valuemax={CHAT_PANEL.MAX_WIDTH}
      style={{
        right: chatWidth - 3,
        // Starts below the header so the strip cannot sit over header controls.
        top: LAYOUT.HEADER_HEIGHT,
        zIndex: Z_INDEX.CHAT_RESIZE_HANDLE,
      }}
      className="group fixed bottom-0 w-1.5 touch-none cursor-col-resize outline-hidden transition-colors duration-150 ease-shell hover:bg-primary/30 focus-visible:bg-primary/50"
      {...handleProps}
    >
      {/* A grip, so the strip reads as draggable before anyone happens to
          hover it. Centred on the strip, not the viewport. */}
      <span
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-1/2 flex h-8 w-3.5 -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center gap-[3px] rounded-full border bg-popover shadow-sm transition-colors duration-150 ease-shell group-hover:border-primary/40 group-focus-visible:border-primary/60"
      >
        <GripDot />
        <GripDot />
        <GripDot />
      </span>
    </div>
  );
};

export default ChatResizeHandle;
